/**
 * CinematicPortalLayout - Global layout wrapper for authenticated internal pages
 * UPDATED: With animated gold particles and film grain overlay
 */

// Luminous cinematic portal background
const PORTAL_BACKGROUND = "https://customer-assets.emergentagent.com/job_e54d5963-4f37-44ed-8ca3-11e6a6219384/artifacts/4zfnbqje_IMG_3607.png";

const CinematicPortalLayout = ({ children, className = "" }) => {
  return (
    <div 
      className={`min-h-screen relative ${className}`} 
      style={{ background: '#0a0500' }}
      data-testid="cinematic-portal-layout"
    >
      {/* ===== LUMINOUS CINEMATIC BACKGROUND LAYERS ===== */}
      
      {/* Layer 1: Background Image - brighter for luminous feel */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${PORTAL_BACKGROUND})`,
          backgroundPosition: 'center 40%',
          opacity: 0.45,
          zIndex: 0,
        }}
        aria-hidden="true"
      />
      
      {/* Layer 2: Gradient overlay - lighter for luminosity */}
      <div 
        className="fixed inset-0"
        style={{
          background: `
            radial-gradient(ellipse 90% 70% at 50% 50%, transparent 0%, rgba(10, 5, 0, 0.6) 60%, rgba(10, 5, 0, 0.9) 100%)
          `,
          zIndex: 1,
        }}
        aria-hidden="true"
      />
      
      {/* Layer 3: Luminous gold accent glow */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 60% 40% at 0% 0%, rgba(255, 215, 0, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 100% 100%, rgba(255, 200, 100, 0.1) 0%, transparent 50%),
            radial-gradient(ellipse 50% 30% at 100% 0%, rgba(255, 215, 0, 0.08) 0%, transparent 40%),
            radial-gradient(ellipse 50% 30% at 0% 100%, rgba(255, 215, 0, 0.08) 0%, transparent 40%)
          `,
          zIndex: 2,
        }}
        aria-hidden="true"
      />

      {/* Layer 4: Film Grain Overlay */}
      <div 
        className="fixed inset-0 pointer-events-none film-grain"
        style={{
          zIndex: 3,
          opacity: 0.04,
          mixBlendMode: 'overlay',
        }}
        aria-hidden="true"
      />

      {/* Layer 5: Animated Gold Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 4 }}>
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="gold-particle"
            style={{
              '--delay': `${i * 0.5}s`,
              '--duration': `${8 + Math.random() * 8}s`,
              '--x-start': `${Math.random() * 100}%`,
              '--x-end': `${Math.random() * 100}%`,
              '--size': `${2 + Math.random() * 4}px`,
              '--opacity': `${0.3 + Math.random() * 0.5}`,
            }}
          />
        ))}
      </div>

      {/* Layer 6: Top edge gold accent line */}
      <div 
        className="fixed top-0 left-0 right-0 h-[2px] pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255, 215, 0, 0.4) 20%, rgba(255, 215, 0, 0.6) 50%, rgba(255, 215, 0, 0.4) 80%, transparent 100%)',
          boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
          zIndex: 5,
        }}
        aria-hidden="true"
      />

      {/* Main Content */}
      <div className="relative" style={{ zIndex: 10 }}>
        {children}
      </div>

      {/* Embedded CSS for animations */}
      <style>{`
        /* Film Grain Animation */
        .film-grain {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E");
          animation: grain 0.5s steps(10) infinite;
        }
        
        @keyframes grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-1%, -1%); }
          20% { transform: translate(1%, 1%); }
          30% { transform: translate(-1%, 1%); }
          40% { transform: translate(1%, -1%); }
          50% { transform: translate(-1%, 0%); }
          60% { transform: translate(1%, 0%); }
          70% { transform: translate(0%, -1%); }
          80% { transform: translate(0%, 1%); }
          90% { transform: translate(-1%, -1%); }
        }

        /* Gold Particle Animation */
        .gold-particle {
          position: absolute;
          width: var(--size);
          height: var(--size);
          background: radial-gradient(circle, rgba(255, 215, 0, var(--opacity)) 0%, transparent 70%);
          border-radius: 50%;
          left: var(--x-start);
          bottom: -10px;
          animation: float-up var(--duration) ease-in-out infinite;
          animation-delay: var(--delay);
          box-shadow: 0 0 6px rgba(255, 215, 0, 0.4);
        }

        @keyframes float-up {
          0% {
            transform: translateY(0) translateX(0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: var(--opacity);
            transform: translateY(-10vh) translateX(10px) scale(1);
          }
          50% {
            transform: translateY(-50vh) translateX(-15px) scale(1.2);
          }
          90% {
            opacity: var(--opacity);
            transform: translateY(-90vh) translateX(10px) scale(0.8);
          }
          100% {
            transform: translateY(-100vh) translateX(0) scale(0);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CinematicPortalLayout;
